import { useState, useEffect } from 'react';
import { usePrivy } from '@privy-io/react-auth';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { MessageSquare, Coins, Check, Loader2, AlertCircle, Copy } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import {
  MESSAGE_PACKAGES,
  PRICE_CURRENCY,
  PAY_CRYPTO,
  PAY_CRYPTO_LABEL,
  type MessagePackage,
} from '@/constants/pricing';
import {
  useCreatePayment,
  usePaymentStatusById,
  usePaymentEstimate,
  usePaymentMinAmount,
  QUERY_KEYS,
} from '@/api';

type Step = 'select' | 'review' | 'pay' | 'success' | 'error';

export function PurchasePage() {
  const { authenticated: isConnected } = usePrivy();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [selectedPackage, setSelectedPackage] = useState<MessagePackage>(MESSAGE_PACKAGES[1]);
  const [step, setStep] = useState<Step>('select');
  const [errorMessage, setErrorMessage] = useState('');
  const [createdPayment, setCreatedPayment] = useState<{
    payment_id: number;
    pay_address: string;
    pay_amount: number;
  } | null>(null);

  const messages = selectedPackage.messages;
  const priceUsd = selectedPackage.priceUsd;

  const createPayment = useCreatePayment();
  const { data: estimate } = usePaymentEstimate(priceUsd, PRICE_CURRENCY, PAY_CRYPTO, priceUsd > 0);
  const { data: minAmount } = usePaymentMinAmount(PAY_CRYPTO, PRICE_CURRENCY, priceUsd > 0);
  const { data: paymentStatus } = usePaymentStatusById(
    createdPayment?.payment_id ?? null,
    step === 'pay' && !!createdPayment?.payment_id
  );

  const estimatedSolRaw = estimate?.data?.estimated_amount;
  const minSolRaw = minAmount?.data?.min_amount;
  const estimatedSol = typeof estimatedSolRaw === 'number' ? estimatedSolRaw : Number(estimatedSolRaw);
  const minSol = typeof minSolRaw === 'number' ? minSolRaw : Number(minSolRaw);
  const isAboveMin = !minSol || !estimatedSol || (Number.isFinite(estimatedSol) && estimatedSol >= minSol);
  const paymentFinished =
    paymentStatus?.data?.payment_status === 'finished' ||
    paymentStatus?.data?.payment_status === 'confirmed';

  const handlePurchase = () => {
    if (!isConnected) return;
    if (!isAboveMin && Number.isFinite(minSol)) {
      setErrorMessage(`Minimum payment is ${minSol} ${PAY_CRYPTO_LABEL}. Please select a larger package.`);
      return;
    }
    setErrorMessage('');
    setStep('review');
  };

  const confirmPurchase = async () => {
    if (!isConnected) return;
    if (!Number.isFinite(estimatedSol) || estimatedSol <= 0) {
      setErrorMessage('Could not get estimated amount. Please try again.');
      return;
    }

    setStep('pay');
    setErrorMessage('');
    setCreatedPayment(null);

    const orderId = `msg_${messages}_${Date.now()}`;

    try {
      const res = await createPayment.mutateAsync({
        price_amount: priceUsd,
        price_currency: PRICE_CURRENCY,
        pay_amount: estimatedSol,
        pay_currency: PAY_CRYPTO,
        order_id: orderId,
        order_description: `${messages} chat messages`,
      });

      const data = res.data;
      if (data?.payment_id && data?.pay_address != null) {
        setCreatedPayment({
          payment_id: data.payment_id,
          pay_address: data.pay_address,
          pay_amount: typeof data.pay_amount === 'number' ? data.pay_amount : Number(data.pay_amount),
        });
      } else {
        throw new Error('Invalid payment response');
      }
    } catch (err: any) {
      setErrorMessage(err.message || 'Failed to create payment');
      setStep('review');
    }
  };

  const copyAddress = () => {
    if (createdPayment?.pay_address) {
      navigator.clipboard.writeText(createdPayment.pay_address);
    }
  };

  const resetPurchase = () => {
    setStep('select');
    setCreatedPayment(null);
  };

  useEffect(() => {
    if (paymentFinished) {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.AUTH.USER_PROFILE });
    }
  }, [paymentFinished, queryClient]);

  if (!isConnected) {
    return (
      <div className="min-h-[calc(100dvh-73px)] sm:min-h-[calc(100vh-73px)] flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 rounded-full bg-[#2a2a35] flex items-center justify-center mx-auto mb-6">
            <MessageSquare className="w-10 h-10 text-[#ff8c42]" />
          </div>
          <h2 className="font-['Poppins',sans-serif] text-2xl font-semibold text-white mb-3">
            Connect Your Wallet
          </h2>
          <p className="text-[#9595a5] mb-6">
            Please connect your wallet to purchase messages
          </p>
        </div>
      </div>
    );
  }

  // Pay step - show address to send crypto, poll until finished
  if (step === 'pay') {
    const waitingForPayment = createdPayment && !paymentFinished;
    const creating = createPayment.isPending && !createdPayment;

    if (creating) {
      return (
        <div className="min-h-[calc(100dvh-73px)] sm:min-h-[calc(100vh-73px)] flex items-center justify-center px-6">
          <div className="text-center max-w-md">
            <Loader2 className="w-16 h-16 text-white animate-spin mx-auto mb-6" />
            <h2 className="font-['Poppins',sans-serif] text-2xl font-semibold text-white mb-3">
              Creating Payment
            </h2>
            <p className="text-[#9595a5]">Preparing your payment...</p>
          </div>
        </div>
      );
    }

    if (paymentFinished) {
      return (
        <div className="min-h-[calc(100dvh-73px)] sm:min-h-[calc(100vh-73px)] flex items-center justify-center px-6">
          <div className="text-center max-w-md">
            <div className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-white" />
            </div>
            <h2 className="font-['Poppins',sans-serif] text-2xl font-semibold text-white mb-3">
              Payment Successful!
            </h2>
            <p className="text-[#9595a5] mb-6">Your messages have been added to your account</p>
            <div className="flex gap-3 mt-8">
              <Button
                onClick={() => navigate('/explore')}
                variant="outline"
                className="flex-1 bg-transparent border-white/[0.08] text-white hover:bg-[#2a2a35]/50"
              >
                Browse Bots
              </Button>
              <Button onClick={resetPurchase} className="flex-1 bg-white hover:opacity-90">
                Buy More
              </Button>
            </div>
          </div>
        </div>
      );
    }

    if (waitingForPayment && createdPayment) {
      return (
        <div className="min-h-[calc(100dvh-73px)] sm:min-h-[calc(100vh-73px)] flex items-center justify-center px-6">
          <div className="text-center max-w-md w-full">
            <div className="w-20 h-20 rounded-full bg-[#2a2a35] flex items-center justify-center mx-auto mb-6">
              <Coins className="w-10 h-10 text-[#ff8c42]" />
            </div>
            <h2 className="font-['Poppins',sans-serif] text-2xl font-semibold text-white mb-3">
              Send {PAY_CRYPTO_LABEL}
            </h2>
            <p className="text-[#9595a5] mb-6">
              Send exactly <strong className="text-white">{createdPayment.pay_amount.toFixed(6)}</strong>{' '}
              {PAY_CRYPTO_LABEL} to the address below
            </p>
            <div className="p-4 rounded-xl bg-[#18181f] border border-white/[0.08] mb-4">
              <p className="text-xs text-[#9595a5] mb-2 text-left">Payment address</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-sm text-white break-all font-mono">
                  {createdPayment.pay_address}
                </code>
                <Button
                  size="icon"
                  variant="outline"
                  onClick={copyAddress}
                  className="shrink-0 bg-transparent border-white/[0.08] text-white hover:bg-[#2a2a35]"
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <div className="flex items-center justify-center gap-2 text-[#9595a5] text-sm">
              <Loader2 className="w-4 h-4 animate-spin" />
              Waiting for payment...
            </div>
            <Button
              onClick={resetPurchase}
              variant="outline"
              className="mt-8 bg-transparent border-white/[0.08] text-white hover:bg-[#2a2a35]/50"
            >
              Cancel
            </Button>
          </div>
        </div>
      );
    }

    // Fallback - e.g. create failed and we're in pay
    return (
      <div className="min-h-[calc(100dvh-73px)] sm:min-h-[calc(100vh-73px)] flex items-center justify-center px-6">
        <div className="text-center">
          <p className="text-[#9595a5] mb-4">Something went wrong. Please try again.</p>
          <Button onClick={resetPurchase} className="bg-white hover:opacity-90">
            Start Over
          </Button>
        </div>
      </div>
    );
  }

  if (step === 'review') {
    return (
      <div className="max-w-2xl mx-auto px-6 py-12">
        <h1 className="font-['Poppins',sans-serif] text-3xl font-semibold text-white mb-8">
          Review Purchase
        </h1>

        <div className="space-y-6">
          <div className="p-6 rounded-xl bg-[#18181f] border border-white/[0.08]">
            <h3 className="font-['Poppins',sans-serif] text-lg font-semibold text-white mb-4">
              Purchase Summary
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center py-3 border-b border-white/[0.08]">
                <span className="text-[#9595a5]">Messages</span>
                <span className="text-white font-medium">{messages}</span>
              </div>
              <div className="flex justify-between items-center py-3 border-b border-white/[0.08]">
                <span className="text-[#9595a5]">Price</span>
                <span className="text-white font-medium">${priceUsd.toFixed(2)} USD</span>
              </div>
              {Number.isFinite(estimatedSol) && (
                <div className="flex justify-between items-center py-3 border-b border-white/[0.08]">
                  <span className="text-[#9595a5]">~{PAY_CRYPTO_LABEL}</span>
                  <span className="text-white font-medium">~{estimatedSol.toFixed(6)}</span>
                </div>
              )}
            </div>
          </div>

          {!isAboveMin && Number.isFinite(minSol) && (
            <div className="p-4 rounded-lg bg-amber-500/20 border border-amber-500/40 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-200">
                Minimum payment is {minSol} {PAY_CRYPTO_LABEL}. Please select a larger package.
              </div>
            </div>
          )}

          {errorMessage && (
            <div className="p-4 rounded-lg bg-red-500/20 border border-red-500/40 text-sm text-red-200">
              {errorMessage}
            </div>
          )}

          <div className="p-4 rounded-lg bg-white/10 border border-white/30 flex items-start gap-3">
            <Coins className="w-5 h-5 text-white flex-shrink-0 mt-0.5" />
            <div className="text-sm text-white">
              You&apos;ll send {PAY_CRYPTO_LABEL} to a payment address. Stay on this page to complete
              the payment.
            </div>
          </div>

          <div className="flex gap-3">
            <Button
              onClick={() => setStep('select')}
              variant="outline"
              className="flex-1 bg-transparent border-white/[0.08] text-white hover:bg-[#2a2a35]/50"
            >
              Cancel
            </Button>
            <Button
              onClick={confirmPurchase}
              disabled={!isAboveMin || createPayment.isPending}
              className="flex-1 bg-white hover:opacity-90 disabled:opacity-50"
            >
              {createPayment.isPending ? 'Creating...' : 'Pay with ' + PAY_CRYPTO_LABEL}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-6 py-12">
      <div className="mb-12">
        <h1 className="font-['Poppins',sans-serif] text-4xl font-semibold text-white mb-3">
          Purchase Messages
        </h1>
        <p className="text-lg text-[#9595a5]">Pay with {PAY_CRYPTO_LABEL} via secure payment</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {MESSAGE_PACKAGES.map((pkg) => (
          <button
            key={pkg.id}
            onClick={() => setSelectedPackage(pkg)}
            className={`relative p-6 rounded-xl border-2 transition-all ${
              selectedPackage.id === pkg.id
                ? 'border-white bg-white/5'
                : 'border-white/[0.08] bg-[#18181f] hover:border-white/[0.15]'
            }`}
          >
            {pkg.popular && (
              <div className="absolute top-0 right-4 -translate-y-1/2">
                <div className="px-3 py-1 rounded-full bg-white text-xs font-medium text-black">
                  Popular
                </div>
              </div>
            )}
            <div className="text-center">
              <div className="text-4xl font-bold text-white mb-2">{pkg.messages}</div>
              <div className="text-sm text-[#9595a5] mb-4">Messages</div>
              <div className="text-lg font-semibold text-white mb-1">
                ${pkg.priceUsd.toFixed(2)}
              </div>
              <div className="text-sm text-[#9595a5]">{PAY_CRYPTO_LABEL}</div>
              {pkg.bonus && (
                <div className="mt-3 text-sm font-medium text-white">{pkg.bonus}</div>
              )}
            </div>
          </button>
        ))}
      </div>

      <Button
        onClick={handlePurchase}
        className="w-full py-6 bg-white hover:opacity-90 text-lg disabled:opacity-50"
      >
        <Coins className="w-5 h-5 mr-2" />
        Purchase {messages} Messages for ${priceUsd.toFixed(2)} ({PAY_CRYPTO_LABEL})
      </Button>
    </div>
  );
}
